import React, { useEffect, useState } from "react";
import { getGroupsHome } from "../../services/groupServices";
import { getSettings } from "../../services/settingsServices";
import { getAdherentsByGroup } from "../../services/dashboardServices";
import { getDojos } from "../../services/dojoServices";

const PlanningCours = () => {
  const [dojos, setDojos] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [saisonActive, setSaisonActive] = useState("");
  const [settings, setSettings] = useState(null);
  const [joursUtilises, setJoursUtilises] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState("");
  const [selectedCours, setSelectedCours] = useState(null);
  const [selectedAnnee, setSelectedAnnee] = useState("");
  const [listeAnnees, setListeAnnees] = useState([]);

  const getLightColorClass = (color) => {
    const colorMap = {
      "bg-orange-400": "bg-orange-200",
      "bg-indigo-400": "bg-indigo-200",
      "bg-purple-400": "bg-purple-200",
      "bg-green-400": "bg-green-200",
      "bg-yellow-400": "bg-yellow-200",
      "bg-blue-400": "bg-blue-200",
      "bg-red-400": "bg-red-200",
      "bg-pink-400": "bg-pink-200",
      "bg-gray-400": "bg-gray-200",
      "bg-cyan-400": "bg-cyan-200",
      "bg-teal-400": "bg-teal-200",
      "bg-emerald-400": "bg-emerald-200",
      "bg-lime-400": "bg-lime-200",
      "bg-amber-400": "bg-amber-200",
      "bg-rose-400": "bg-rose-200",
      "bg-violet-400": "bg-violet-200",
      "bg-sky-400": "bg-sky-200",
      "bg-fuchsia-400": "bg-fuchsia-200",
      "bg-zinc-400": "bg-zinc-200",
      "bg-gray-500": "bg-gray-300",
    };
    return colorMap[color] || "bg-gray-200";
  };

  const tailwindBgToHex = {
    "bg-red-400": "#f87171",
    "bg-orange-400": "#fb923c",
    "bg-indigo-400": "#818cf8",
    "bg-purple-400": "#a78bfa",
    "bg-green-400": "#4ade80",
    "bg-yellow-400": "#facc15",
    "bg-blue-400": "#60a5fa",
    "bg-pink-400": "#f472b6",
    "bg-gray-400": "#9ca3af",
    "bg-cyan-400": "#22d3ee",
    "bg-teal-400": "#2dd4bf",
    "bg-emerald-400": "#34d399",
    "bg-lime-400": "#a3e635",
    "bg-amber-400": "#fbbf24",
    "bg-rose-400": "#fb7185",
    "bg-violet-400": "#c084fc",
    "bg-sky-400": "#38bdf8",
    "bg-fuchsia-400": "#e879f9",
    "bg-zinc-400": "#a1a1aa",
    "bg-gray-500": "#6b7280",
  };

  const getHexColor = (twClass) => {
    return tailwindBgToHex[twClass] || "#e5e7eb"; // gris clair par défaut
  };

  useEffect(() => {
    const orderedWeekDays = [
      "Lundi",
      "Mardi",
      "Mercredi",
      "Jeudi",
      "Vendredi",
      "Samedi",
      "Dimanche",
    ];

    const extractAnneesUnique = (groupes) => {
      const annees = new Set();

      groupes.forEach((g) => {
        const champ = g.annees_naissance;
        if (!champ) return;

        champ.split("/").forEach((fragment) => {
          fragment = fragment.trim();

          // Cas 1 : plusieurs années séparées par des tirets (ex : "2015-2014-2013")
          if (/^(\d{4}-)+\d{4}$/.test(fragment)) {
            fragment.split("-").forEach((a) => {
              if (/^\d{4}$/.test(a)) annees.add(a);
            });
          }
          // Cas 2 : année simple
          else if (/^\d{4}$/.test(fragment)) {
            annees.add(fragment);
          }
          // Cas 3 : plage d'années (ex : "2014-2015")
          else if (/^\d{4}-\d{4}$/.test(fragment)) {
            const [a1, a2] = fragment.split("-");
            annees.add(a1);
            annees.add(a2);
          }
          // Cas 4 : "+14 ans" ou "+14ans"
          else if (/^\+?\d+\s?ans$/i.test(fragment)) {
            const num = fragment.match(/\d+/)?.[0];
            if (num) annees.add(`+${num} ans`);
          }
        });
      });

      return Array.from(annees).sort((a, b) => {
        const nA = parseInt(a);
        const nB = parseInt(b);
        if (!isNaN(nA) && !isNaN(nB)) return nB - nA;
        return a.localeCompare(b);
      });
    };

    const extractJoursFromGroupes = (groupes) => {
      const jours = new Set();
      groupes.forEach((g) => {
        for (let i = 1; i <= 6; i++) {
          const horaire = g[`horaire${i}`];
          if (horaire) {
            const [jour] = horaire.split(" ");
            if (jour) jours.add(jour);
          }
        }
      });

      return Array.from(jours).sort(
        (a, b) => orderedWeekDays.indexOf(a) - orderedWeekDays.indexOf(b)
      );
    };

    const fetchData = async () => {
      try {
        const settingsData = await getSettings();
        if (settingsData && settingsData[0]?.saison_active) {
          setSaisonActive(settingsData[0].saison_active);
          setSettings(settingsData[0]);
        }

        const groupsData = await getGroupsHome();
        const adherentsData = await getAdherentsByGroup();
        if (groupsData?.length > 0) {
          const groupesActifs = groupsData
            .filter((g) => g.is_active)
            .map((g) => {
              const match = adherentsData.find((a) => a.nom === g.nom);
              return {
                ...g,
                adherentCount: match?.adherentCount || 0,
              };
            });

          setGroupes(groupesActifs);
          const joursTrouves = extractJoursFromGroupes(groupesActifs);
          setJoursUtilises(joursTrouves);
          const annees = extractAnneesUnique(groupesActifs);
          setListeAnnees(annees);

          const groupesFiltres = selectedDojo
            ? groupes.filter((g) => g.dojo_id === parseInt(selectedDojo))
            : groupes;
        }
      } catch (error) {
        console.error("Erreur chargement planning", error);
      }
    };

    const fetchDojos = async () => {
      try {
        const data = await getDojos();
        setDojos(data);
      } catch (error) {
        console.error("Erreur récupération des dojos", error);
      }
    };

    fetchData();
    fetchDojos();
  }, []);

  const getCoursParJour = (jour) => {
    const groupesFiltres = selectedDojo
      ? groupes.filter((g) => g.dojo_id === parseInt(selectedDojo))
      : groupes;

    const groupesAvecAnnee = selectedAnnee
      ? groupesFiltres.filter((g) => {
          const fragments = g.annees_naissance
            ?.replace(/\s+/g, "")
            .split("/")
            .map((frag) => {
              if (/^\d{4}$/.test(frag)) return frag;
              if (/^\d{4}-\d{4}$/.test(frag)) return frag.split("-");
              if (/^\+?\d+ans$/i.test(frag)) {
                const n = frag.match(/\d+/)?.[0];
                return `+${n}ans`;
              }
              return frag;
            })
            .flat()
            .filter(Boolean);

          return fragments?.some((frag) =>
            frag.replace(/\s+/g, "").includes(selectedAnnee.replace(/\s+/g, ""))
          );
        })
      : groupesFiltres;

    const result = [];
    groupesAvecAnnee.forEach((groupe) => {
      for (let i = 1; i <= 6; i++) {
        const horaire = groupe[`horaire${i}`];
        if (horaire && horaire.startsWith(jour)) {
          const [_, plage] = horaire.split(" ");
          result.push({ ...groupe, horaire: plage });
        }
      }
    });

    return result.sort((a, b) => a.horaire.localeCompare(b.horaire));
  };

  return (
    <div className="p-6  bg-lightBackground dark:bg-darkBackground text-black dark:text-white font-montserrat">
      {/* En-tête avec logo + titre */}
      <div className="flex items-center justify-center gap-6 mb-10 flex-wrap">
        {settings?.logo_club && (
          <img
            src={settings.logo_club}
            alt="Logo Club"
            className="w-auto h-28 drop-shadow-md"
          />
        )}
        <div className="text-center">
          <h1 className="text-3xl lg:text-4xl font-extrabold uppercase tracking-wide">
            Planning des cours
          </h1>
          <h2 className="text-xl font-light mt-1">Saison {saisonActive}</h2>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center justify-center gap-6">
        <div className="mb-6 text-center">
          <label className="mr-2 font-semibold">Filtrer par Dojo :</label>
          <select
            className="border border-gray-300 rounded px-2 py-1 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedDojo}
            onChange={(e) => setSelectedDojo(e.target.value)}
          >
            <option value="">Tous les dojos</option>
            {dojos.map((dojo) => (
              <option key={dojo.id} value={dojo.id}>
                {dojo.dojo_nom}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6 text-center">
          <label className="mr-2 font-semibold">Filtrer par Année :</label>
          <select
            className="border border-gray-300 rounded px-2 py-1 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedAnnee}
            onChange={(e) => setSelectedAnnee(e.target.value)}
          >
            <option value="">Toutes les années</option>
            {listeAnnees.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Grille par jour */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center w-fit mx-auto">
        {joursUtilises.map((jour) => (
          <div key={jour}>
            <h3 className="text-lg md:text-xl font-bold uppercase border-b border-gray-300 pb-2 mb-4">
              {jour}
            </h3>
            {getCoursParJour(jour).map((cours, idx) => (
              <div
                onClick={() => setSelectedCours(cours)}
                className={`relative cursor-pointer rounded-md shadow-md text-sm mb-4 px-3 py-2 text-black ${
                  cours.color_groupe || "bg-gray-200"
                }`}
              >
                {/* Badge "Complet" si quota atteint */}
                {cours.quota_groupe > 0 &&
                  cours.adherentCount >= cours.quota_groupe && (
                    <span className="absolute bottom-1 right-1 bg-red-600 text-white text-[10px] px-2 py-0 rounded-full font-bold uppercase shadow">
                      Complet
                    </span>
                  )}

                <div className=" text-xs font-semibold uppercase tracking-wide bg-white bg-opacity-50 mb-2">
                  {cours.horaire}
                </div>
                <div className="text-base font-extrabold uppercase font-montserrat leading-snug">
                  {cours.nom}
                </div>
                <div className="text-sm mt-1">
                  {[cours.enseignant1, cours.enseignant2, cours.enseignant3]
                    .filter(Boolean)
                    .join(" & ")}
                </div>
                <div className="text-xs font-semibold mt-1">
                  {cours.annees_naissance}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      {selectedCours && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center px-4"
          onClick={() => setSelectedCours(null)}
        >
          <div
            className="relative rounded-lg w-full max-w-xl shadow-xl overflow-hidden bg-white dark:bg-gray-900"
            style={{
              borderLeft: `15px solid ${getHexColor(
                selectedCours.color_groupe
              )}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 pl-8 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900">
              <p className="text-sm text-gray-600 dark:text-gray-800 mb-4 italic bg-slate-300 p-2 mt-2 font-bold">
                {dojos.find((d) => d.id === selectedCours.dojo_id)?.dojo_nom}
              </p>

              <h2 className="text-2xl font-bold uppercase mb-4">
                {selectedCours.nom}
              </h2>
              <h3 className="text-sm font-bold uppercase mb-4">
                {selectedCours.annees_naissance}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <h3 className="font-semibold text-gray-700 dark:text-gray-800 pl-2 mb-1 bg-gray-100">
                    Horaires
                  </h3>
                  {[...Array(6)].map((_, i) => {
                    const horaire = selectedCours[`horaire${i + 1}`];
                    return horaire ? (
                      <p key={i} className="ml-2">
                        • {horaire}
                      </p>
                    ) : null;
                  })}
                </div>

                <div>
                  <h3 className="font-semibold text-gray-700  dark:text-gray-800 pl-2 mb-1 bg-gray-100">
                    Tarifs
                  </h3>
                  <p>Licence : {selectedCours.tarif_licence} €</p>
                  <p>Cotisation : {selectedCours.tarif_cotisation} €</p>
                  <p className="font-semibold mt-1">
                    Total :{" "}
                    {parseFloat(selectedCours.tarif_licence || 0) +
                      parseFloat(selectedCours.tarif_cotisation || 0)}{" "}
                    €
                  </p>
                </div>
              </div>

              {selectedCours.info_groupe && (
                <div className="mb-4 text-sm">
                  <h3 className="font-semibold text-gray-700 mb-1 dark:text-gray-800 pl-2 bg-gray-100">
                    Informations complémentaires
                  </h3>
                  <div
                    className="prose dark:prose-invert text-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: selectedCours.info_groupe,
                    }}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <h3 className="font-semibold text-gray-700 dark:text-gray-800 pl-2 mb-1 bg-gray-100">
                    Enseignants
                  </h3>
                  <p>
                    {[
                      selectedCours.enseignant1,
                      selectedCours.enseignant2,
                      selectedCours.enseignant3,
                      selectedCours.enseignant4,
                      selectedCours.enseignant5,
                      selectedCours.enseignant6,
                    ]
                      .filter(Boolean)
                      .join(" & ")}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 dark:text-gray-800 pl-2 mb-1 bg-gray-100">
                    Effectif
                  </h3>
                  <p>
                    {selectedCours.adherentCount} inscrit(s) /{" "}
                    {selectedCours.quota_groupe}{" "}
                    <span className="italic">
                      (
                      {Math.round(
                        (selectedCours.adherentCount /
                          selectedCours.quota_groupe) *
                          100 || 0
                      )}
                      % rempli)
                    </span>
                  </p>

                  {selectedCours.quota_groupe > 0 &&
                    selectedCours.adherentCount >=
                      selectedCours.quota_groupe && (
                      <div className="mt-2 inline-block bg-red-600 text-white text-xs font-bold uppercase px-3 py-1 rounded shadow">
                        Complet
                      </div>
                    )}
                </div>
              </div>

              <div className="text-right mt-6">
                <button
                  onClick={() => setSelectedCours(null)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold transition"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningCours;
